import { localMultiplayer } from '../multiplayer/localConfig';
import { formatDMY } from '../b2Editor/utils/formatString';
import '../css/Lobby.scss'
import { game } from '../Game';
import { skinURL } from '../multiplayer/hud';
import { adminStartLoadLevel, allPlayersReady, currentRoomName, kickPlayer, localPlayerName, LOBBY_STATE, multiplayerState, returnToMultiplayer, setLobbyStateReady } from '../multiplayer/multiplayerManager';
import { multiplayerAtlas } from '../multiplayer/rippleCharacter';
import { Settings } from '../Settings';
import { localize } from '../utils/Localization';
import { LOCK_ICON, showLobbySettingsForm } from './lobbyBrowser';

const PENCIL_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3l3 3-7 7H3v-3zM11 2l1-1 3 3-1 1z"/></svg>';

let lobby = null;
let inviteLink = '';

const copyText = async text => {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		const field = document.createElement('textarea');
		field.value = text;
		document.body.appendChild(field);
		field.select();
		const copied = document.execCommand('copy');
		field.remove();
		return copied;
	}
}

const copyWithFeedback = async (text, holder, label, restoreText) => {
	if (!text || !await copyText(text)) return;
	const copied = localize('multiplayer_copied');
	holder.classList.add('copied');
	label.innerText = `${copied.charAt(0).toUpperCase()}${copied.slice(1)}!`;
	clearTimeout(holder.copiedTimer);
	holder.copiedTimer = setTimeout(() => {
		holder.classList.remove('copied');
		label.innerText = restoreText;
	}, 2000);
}

const updateInviteLink = () => {
	const code = multiplayerState.lobby;
	const link = lobby.querySelector('.text-url');
	if(!code){
		inviteLink = link.value = '';
		return;
	}
	if(!Settings.onPoki){
		const invite = new URL(window.location.pathname, window.location.origin);
		invite.searchParams.set('lobbyID', code);
		if (localMultiplayer) invite.searchParams.set('multiplayerLocal', '1');
		inviteLink = link.value = invite.href;
	} else {
		PokiSDK.shareableURL({lobbyID: code}).then(url => {
			if (multiplayerState.lobby === code) inviteLink = link.value = url;
		}).catch(err => console.warn('Could not create invite link', err));
	}
}

export const generateLobby = () => {
	if(!lobby){
		const htmlStructure = /*html*/`
			<div class="title">
				${localize('multiplayer_lobby')}
			</div>

			<div class="lobby-meta">
				<div class="lobby-code-holder">
					<div class="lobby-code-label">${localize('multiplayer_lobbycode')}:</div>
					<div class="lobby-code" role="button" aria-label="${localize('multiplayer_copycode')}"></div>
				</div>
				<div class="lobby-badges">
					<div class="badge visibility"></div>
					<div class="badge locked">${LOCK_ICON}</div>
					<div class="badge player-count"></div>
					<div class="badge lobby-edit" role="button" aria-label="${localize('multiplayer_lobbysettings')}">${PENCIL_ICON}</div>
				</div>
			</div>

			<div class="level-info">
				<div class="thumb select admin">
					<div class="selectBut">${localize('mainmenu_selectlevel')}</div>
					<div class="hostPicking">${localize('mainmenu_nolevel')}</div>
				</div>
				<div class="text-holder select">
					<div class="text-level-name">Level Name Goes Here</div>
					<div class="level-author">
						<div class="text-level-by">${localize('mainmenu_by')}:</div>
						<div class="text-author">Author Name</div>
					</div>
					<div class="tags">
					</div>
					<div class="description-holder">
						<div class="description"></div>
					</div>
					<div class="level-published">
						<div class="text-published">Publ.</div>
						<div class="text-date-published">10 Mar 2020</div>
						<div class="text-updated">Upd.</div>
						<div class="text-date-updated">10 Mar 2020</div>
					</div>
				</div>
			</div>

			<div class="copy-url">
				<div class="level-link-text">${localize('multiplayer_invitelink')}:</div>
				<input class="text-url" readonly aria-label="${localize('multiplayer_invitelink')}">
				<div class="copy-button"></div>
			</div>

			<div class="player-list">
				<div class="players-title">${localize('mainmenu_players')}</div>
				<div class="entries">
					<div class="entry entry-template">
						<div class="position">
							<div class="profile"></div>
							<div class="text-position">1st</div>
						</div>
						<div class="text-player-name">Smerik</div>
						<div class="kick-but">${localize('mainmenu_kick')}</div>
						<div class="player-status connecting">${localize('multiplayer_host')}</div>
					</div>
				</div>
			</div>

			<div class="joining">${localize('multiplayer_joining')}</div>

			<div class="nav-buttons">
				<div class="leave-but">${localize('mainmenu_leave')}</div>
				<div class="ready-but">${localize('mainmenu_ready')}</div>
			</div>
		`;

		lobby = document.createElement('div');
		lobby.classList.add('lobby');
		lobby.innerHTML = htmlStructure;

		const levelSelectButton = lobby.querySelector('.selectBut');
		levelSelectButton.onclick = ()=>{
			// we need to set the gameState twice, because the first time we make sure we dont leave multiplayer, then
			// openSinglePlayer resets it, so we set it again
			game.gameState = game.GAMESTATE_MULTIPLAYER_LEVELSELECT;
			game.openSinglePlayer();
			game.gameState = game.GAMESTATE_MULTIPLAYER_LEVELSELECT;
		}

		const copyUrl = lobby.querySelector('.copy-url');
		copyUrl.querySelector('.copy-button').onclick = () =>
			copyWithFeedback(inviteLink, copyUrl, copyUrl.querySelector('.level-link-text'), `${localize('multiplayer_invitelink')}:`);

		const codeHolder = lobby.querySelector('.lobby-code-holder');
		codeHolder.querySelector('.lobby-code').onclick = () =>
			copyWithFeedback(multiplayerState.lobby, codeHolder, codeHolder.querySelector('.lobby-code-label'), `${localize('multiplayer_lobbycode')}:`);

		lobby.querySelector('.lobby-edit').onclick = () => {
			if (multiplayerState.admin) showLobbySettingsForm(true);
		};

		const navButtons = lobby.querySelector('.nav-buttons');
		const leaveButton = navButtons.querySelector('.leave-but');
		leaveButton.onclick = () => returnToMultiplayer();
	}

	updateLobbyUI();

	return lobby;
}

export const updateLobbyUI = () => {
	if (!lobby) return;
	const connecting = multiplayerState.lobbyState === LOBBY_STATE.CONNECTING || !multiplayerState.lobby;
	lobby.classList.toggle('connecting', connecting);

	// LOBBY
	lobby.querySelector('.title').innerText = connecting ? localize('multiplayer_lobby') : currentRoomName();
	const settings = multiplayerState.lobbySettings;
	lobby.querySelector('.lobby-code').innerText = multiplayerState.lobby;
	const visibility = lobby.querySelector('.badge.visibility');
	visibility.innerText = localize(settings.public ? 'multiplayer_public' : 'multiplayer_private');
	lobby.querySelector('.badge.locked').hidden = !settings.hasPassword;
	lobby.querySelector('.badge.player-count').innerText = `${multiplayerState.peersConnected + 1}/${settings.maxPlayers}`;
	lobby.querySelector('.lobby-edit').hidden = !multiplayerState.admin;
	if (lobby.querySelector('.text-url').dataset.lobby !== multiplayerState.lobby) {
		lobby.querySelector('.text-url').dataset.lobby = multiplayerState.lobby;
		updateInviteLink();
	}

	// LEVEL DATA
	const levelData = multiplayerState.selectedLevelData;

	const thumb = lobby.querySelector('.thumb');
	const textHolder = lobby.querySelector('.text-holder');
	const levelSelectButton = lobby.querySelector('.selectBut');
	const entries = lobby.querySelector('.entries');

	if(levelData){
		thumb.classList.remove('select');
		textHolder.classList.remove('select');

		const thumbSrc = localMultiplayer && levelData.id === 'local-multiplayer-lab' ? 'assets/images/portraits/characterselect1.png' : `${Settings.STATIC}/${levelData.thumb_big_md5}.png`;
		thumb.style.backgroundImage = `url(${thumbSrc})`;

		const title = lobby.querySelector('.text-level-name');
		title.innerText = levelData.title;
		const author = lobby.querySelector('.text-author');
		author.innerText = levelData.author.username;

		const description = lobby.querySelector('.description');
		description.innerText = levelData.description;


		const publishedDateDiv = lobby.querySelector('.level-published');

		const publishedDate = publishedDateDiv.querySelector('.text-date-published');
		publishedDate.innerText = formatDMY(levelData.created_at);

		if(formatDMY(levelData.updated_at) === publishedDate.innerText){
			publishedDateDiv.classList.add('no-update');
		}else{
			publishedDateDiv.classList.remove('no-update');
			const updatedDate = publishedDateDiv.querySelector('.text-date-updated');
			updatedDate.innerText = formatDMY(levelData.updated_at);
		}
		levelSelectButton.innerText = localize('mainmenu_changelevel');
	}else{
		thumb.classList.add('select');
		textHolder.classList.add('select');
		thumb.style.backgroundImage = 'unset';
		levelSelectButton.innerText = localize('mainmenu_selectlevel');
	}

	if(multiplayerState.admin){
		thumb.classList.add('admin');
	}else{
		thumb.classList.remove('admin');
	}

	// PLAYERS

	const template = entries.querySelector('.entry-template');
	template.style.display = 'none';

	const myPlayer = {
		admin: multiplayerState.admin,
		playerState: {
			name: localPlayerName(),
			lobbyState: multiplayerState.lobbyState,
		},
		skinBlob: multiplayerState.skinBlob
	}

	while(entries.children.length>1){
		entries.removeChild(entries.children[1]);
	}

	const otherPlayers = Object.entries(multiplayerState.players).map(([id, player]) => ({id, player}));
	const players = connecting ? [{player: myPlayer}] : [{player: myPlayer}, ...otherPlayers];

	let playersReady = 1;

	players.forEach(({ id, player: { admin, playerState, skinBlob, reconnecting, introduced } }, index) => {
		const entry = template.cloneNode(true);
		entry.style.display = 'flex';
		entry.classList.remove('entry-template');

		const profile = entry.querySelector('.profile');

		if(skinBlob){
			const resolution = 1.8;
			profile.style.backgroundImage = `url(${skinURL(skinBlob)})`;
			profile.style.backgroundSize = `${1024 / resolution}px`;
			const frame = multiplayerAtlas.frames.profile.frame;
			profile.style.backgroundPosition = `${-frame.x / resolution}px ${-frame.y / resolution}px`;
		}else{
			profile.style.backgroundImage = '';
		}

		const username = entry.querySelector('.text-player-name')
		username.innerText = playerState.name;

		if(index === 0){
			entry.classList.add('me');
		}

		const status = entry.querySelector('.player-status');

		status.classList.remove('connecting');

		if(reconnecting || (index > 0 && !introduced) || playerState.lobbyState === LOBBY_STATE.CONNECTING){
			status.innerText = localize(reconnecting ? 'multiplayer_reconnecting' : 'mainmenu_connecting');
			status.classList.add('connecting');
		}else if(admin){
			status.innerText = localize('multiplayer_host');
			status.classList.add('waiting');
		} else if(playerState.lobbyState === LOBBY_STATE.READY){
			status.innerText = localize('mainmenu_ready');
			status.classList.add('ready');
		} else {
			status.innerText = localize('mainmenu_waiting');
			status.classList.add('waiting');
		}

		const kickButton = entry.querySelector('.kick-but');
		if(!multiplayerState.admin || index === 0){
			kickButton.style.visibility = 'hidden';
		} else {
			kickButton.setAttribute('role', 'button');
			kickButton.setAttribute('aria-label', `${localize('mainmenu_kick')} ${playerState.name}`);
			kickButton.onclick = () => {
				kickPlayer(id);
			}
		}

		if(playerState.lobbyState === LOBBY_STATE.READY){
			playersReady++;
		}

		entries.appendChild(entry);
	});

	// BUTTONS
	const navButtons = lobby.querySelector('.nav-buttons');

	const readyButton = navButtons.querySelector('.ready-but');

	readyButton.classList.remove('ready');

	readyButton.onclick = ()=>{};

	if(connecting){
		readyButton.innerText = localize('mainmenu_connecting');
		readyButton.setAttribute('aria-disabled', 'true');
		return;
	}

	const canStart = players.length > 1 && allPlayersReady() && !!multiplayerState.selectedLevelData;
	const pickLevel = players.length > 1 && !multiplayerState.selectedLevelData;
	readyButton.setAttribute('aria-disabled', String(multiplayerState.admin && !canStart && !pickLevel));

	if(multiplayerState.admin){
		if(players.length === 1){
			readyButton.innerText = localize('multiplayer_waitingforplayers');
		}else if(pickLevel){
			readyButton.innerText = localize('mainmenu_selectlevel');
			readyButton.onclick = () => lobby.querySelector('.selectBut').click();
		}else if(canStart){
			readyButton.innerText = localize('mainmenu_start');
			readyButton.classList.add('ready');

			readyButton.onclick = () => {
				adminStartLoadLevel();
			}

		}else {
			readyButton.innerText = `${localize('mainmenu_ready')} ${playersReady - 1}/${players.length - 1}`;
		}
	}else{
		if(myPlayer.playerState.lobbyState === LOBBY_STATE.READY){
			readyButton.innerText = `${localize('mainmenu_waiting')} ${playersReady}/${players.length}`;
		}else{
			readyButton.classList.add('ready');
			readyButton.innerText = localize('mainmenu_ready');
		}

		readyButton.onclick = () => {
			setLobbyStateReady(!(myPlayer.playerState.lobbyState === LOBBY_STATE.READY))
		}
	}
}
